import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfig } from "../../../../../context/ConfigContext";
import { Cart, CategoryItem } from "../../../../../interfaces/Catalogue";
import { updateCart } from "../../../../../store/cart/cartActions";
import { getIcon, IconType } from "../../../../../theme/icons";
import {
  getPurchaseOfferMessage,
  getRemiseMessage,
} from "../../../../../utils/loyaltyUtils";
import {
  calculateArticleTotalPriceWithOffers,
  calculatediscountedPrice
} from "../../../../../utils/priceUtils";
import RewardClaimButton from "../../../RewardClaimButton/RewardClaimButton";
import "./OrderItemHeader.scss";

interface OrderItemHeaderProps {
  orderItemIndex: number;
  categoryItem: CategoryItem;
  restaurantId?: number;
  canEditQuantity?: boolean;
  canApplyArticleDiscount?: boolean;
  rewardClaimDisabled?: boolean;
  userLoyalityPoints?: number;
  totalLoyaltyPtsConsomed?: number;
}

const OrderItemHeader: React.FC<OrderItemHeaderProps> = ({
  orderItemIndex,
  categoryItem,
  restaurantId,
  canEditQuantity = false,
  canApplyArticleDiscount = false,
  rewardClaimDisabled = false,
  userLoyalityPoints,
  totalLoyaltyPtsConsomed,
}) => {
  const { config } = useConfig();
  const quantity = categoryItem?.quantity || 0;
  const [total, setTotal] = useState((0.0).toFixed(2));
  const dispatch = useDispatch();
  const restaurantCarts = useSelector(
    (state: { cart: Cart }) => state.cart.restaurantCarts
  );

  // const [isRemiseActive, setIsRemiseActive] = useState<boolean>(
  //   categoryItem.isRemiseActive || false
  // );

  useEffect(() => {
    let calculatedTotal = 0;

    if (categoryItem.purchaseOffers) {
      calculatedTotal = calculateArticleTotalPriceWithOffers(
        categoryItem.purchaseOffers,
        categoryItem.finalUnitPrice ?? 0,
        quantity
      );
    } else {
      calculatedTotal =
        (categoryItem?.finalUnitPrice ?? 0) * (categoryItem?.quantity ?? 0);
    }

    setTotal(calculatedTotal.toFixed(2));
  }, [categoryItem.quantity, categoryItem.finalUnitPrice]);

  const __updateCart = (actionType: "INCREASE" | "DECREASE") => {
    if (!restaurantId) return;

    let curreSavedCart = restaurantCarts;
    let category: CategoryItem =
      restaurantCarts[restaurantId]?.categories[orderItemIndex];

    switch (actionType) {
      case "INCREASE":
        if (
          curreSavedCart[restaurantId] &&
          Array.isArray(curreSavedCart[restaurantId].categories) &&
          curreSavedCart[restaurantId].categories[orderItemIndex]
        ) {
          let category =
            curreSavedCart[restaurantId].categories[orderItemIndex];
          category.quantity = (category.quantity || 0) + 1;
          curreSavedCart[restaurantId].categories[orderItemIndex] = category;
        } else {
          console.warn("Category not found or invalid data structure");
        }
        break;

      case "DECREASE":
        if (category?.quantity && category?.quantity > 1) {
          category.quantity = (category?.quantity ?? 0) - 1;
          curreSavedCart[restaurantId].categories[orderItemIndex] = category;
        } else {
          if ((category?.quantity ?? 0) - 1 <= 0) {
            let cats = curreSavedCart[restaurantId].categories;
            cats?.splice(orderItemIndex, 1);
            curreSavedCart[restaurantId].categories = cats;

            let selectedCategories = curreSavedCart[restaurantId].categories;
            if (selectedCategories?.length === 0) {
              delete curreSavedCart[restaurantId];
            }
          }
        }
        break;

      default:
        throw new Error(`Unsupported action type: ${actionType}`);
    }

    dispatch(updateCart(curreSavedCart));
  };

  const updateArticleLoyality = (isRemiseActive: boolean) => {
    if (restaurantId) {
      // Clone restaurantCarts to avoid mutation
      let updatedSavedCart = { ...restaurantCarts };

      // Find the specific category and order
      const orderCategoryItem = updatedSavedCart[restaurantId]?.categories.find(
        (category) => category.id === categoryItem.id
      );

      if (orderCategoryItem) {
        // setIsRemiseActive(!categoryItem.isRemiseActive);

        categoryItem.isRemiseActive = isRemiseActive || false;

        categoryItem.discountedPrice = isRemiseActive
          ? calculatediscountedPrice(
            categoryItem?.unitPrice || 0,
            categoryItem?.rewardDiscountType,
            categoryItem?.rewardDiscountValue
          ) +
          ((categoryItem?.finalUnitPrice || 0) -
            (categoryItem?.unitPrice || 0))
          : null;
      }

      // Dispatch updated cart
      dispatch(updateCart(updatedSavedCart));
    }
  };

  const newTotalUserPts =
    (totalLoyaltyPtsConsomed || 0) +
    categoryItem.rewardTotalPtRequired * quantity;

  return (
    <div className="menu-header">
      <div>
        <div className="col-1-header">
          <p className="menu-name">
            {`${!canEditQuantity ? categoryItem?.quantity + "x : " : ""}${categoryItem?.designation}`}
          </p>

          <img
            className="menu-img"
            src={categoryItem?.imgUrl || ""}
            alt={categoryItem?.designation}
          />
        </div>

        {categoryItem?.purchaseOffers && (
          <div className="discountContainer">
            <p className="discountMessage">
              {getPurchaseOfferMessage(categoryItem.purchaseOffers)}
            </p>
          </div>
        )}

        {canApplyArticleDiscount && categoryItem?.rewardTotalPtRequired && (
          <div className="discountContainer">
            <p className="discountMessage">{getRemiseMessage(categoryItem)}</p>
            {canApplyArticleDiscount && !categoryItem?.order && (
              <RewardClaimButton
                articleClaimed={categoryItem.isRemiseActive || false}
                selectedMenuItem={categoryItem}
                updateArticleLoyality={updateArticleLoyality}
                isClaimDisabled={rewardClaimDisabled || false}
              />
            )}
          </div>
        )}
      </div>
      <div className="col-2-header">
        {canEditQuantity ? (
          <div className="slot-menu">
            {categoryItem?.quantity === 1
              ? getIcon(IconType.TRASH, {
                color: config?.primaryColor,
                onClick: () => __updateCart("DECREASE"),
              })
              : getIcon(IconType.MINUS, {
                color: config?.primaryColor,
                onClick: () => __updateCart("DECREASE"),
              })}
            <p className="quantity">{categoryItem?.quantity}</p>
            {getIcon(IconType.PLUS, {
              color: config?.primaryColor,
              onClick: () => __updateCart("INCREASE"),
            })}
          </div>
        ) : (
          <div className="slot-menu"></div>
        )}
        <div className="price-container">
          <p
            className={
              categoryItem?.discountedPrice != null
                ? "price discount-price"
                : "price"
            }
          >
            {`${total} € `}
          </p>
          {categoryItem?.discountedPrice != null && (
            <p className="price">
              {categoryItem.discountedPrice === 0
                ? "  / Gratuit"
                : `  / ${(categoryItem.discountedPrice * (categoryItem?.quantity || 0)).toFixed(2)} €`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderItemHeader;
